import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { AuthAPI } from "../../api/authAPI";
import AuthLayout from "../../components/AuthLayout";
import { EMAIL_USER_TYPE } from "../../utils/constants";
import { getPostAuthDestination } from "../../utils/onboardingRouting";
import { useAuth } from "../../context/AuthContext";
import { Box } from "@mui/material";

// Module-level map: survives React StrictMode's mount→unmount→remount cycle,
// and deduplicates auth_code exchange requests across concurrent mounts.
const _inflightAuthCodeRequests = new Map();

const getUnauthorizedMessage = (reason) => {
  if (reason === "pending") {
    return "Account pending approval";
  }

  if (reason === "deleted") {
    return "Account not found";
  }

  return "Something went wrong";
};

export default function AuthCallback() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setEmailUserAuthToken, setAdminAuthToken } = useAuth();
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isActive = true;

    const params = new URLSearchParams(window.location.search);
    const authCode = params.get("auth_code");
    const unauthorized = params.get("unauthorized");
    const reason = params.get("reason");

    if (unauthorized) {
      if (isActive) {
        setErrorMessage(getUnauthorizedMessage(reason));
        setIsLoading(false);
      }
      return () => {
        isActive = false;
      };
    }

    if (!authCode) {
      if (isActive) {
        setErrorMessage("Something went wrong");
        setIsLoading(false);
      }
      return () => {
        isActive = false;
      };
    }

    const getAuthExchangeRequest = () => {
      if (!_inflightAuthCodeRequests.has(authCode)) {
        const request = AuthAPI.googleAuthCodeLogin({
          auth_code: authCode,
        }).finally(() => {
          _inflightAuthCodeRequests.delete(authCode);
        });
        _inflightAuthCodeRequests.set(authCode, request);
      }
      return _inflightAuthCodeRequests.get(authCode);
    };

    const login = async () => {
      try {
        const authResponse = await getAuthExchangeRequest();
        if (!isActive) {
          return;
        }
        const token = authResponse?.token || authResponse?.data?.token;
        const userType =
          authResponse?.user_type ||
          authResponse?.data?.user_type ||
          EMAIL_USER_TYPE;

        if (!token) {
          throw new Error("Token missing");
        }

        await queryClient.cancelQueries({ queryKey: ["currentUser"] });
        queryClient.removeQueries({ queryKey: ["currentUser"] });

        [localStorage, sessionStorage].forEach((storage) => {
          storage.removeItem("Token");
          storage.removeItem("authToken");
          storage.removeItem("user_type");
        });

        localStorage.setItem("authToken", token);
        localStorage.setItem("user_type", userType);
        setEmailUserAuthToken(token);
        setAdminAuthToken(null);
        await queryClient.prefetchQuery({
          queryKey: ["currentUser"],
          queryFn: () => AuthAPI.getCurrentUser(),
        });

        const meResponse = await AuthAPI.getCurrentUser();
        const destination = getPostAuthDestination(meResponse?.data);

        if (!isActive) {
          return;
        }
        navigate(destination || "/register", { replace: true });
        // Do not call setIsLoading(false) here — navigate() will unmount this
        // component before the next render cycle, preventing an error flash.
      } catch (error) {
        if (!isActive) {
          return;
        }
        setErrorMessage(
          error?.response?.data?.message || "Something went wrong",
        );
        setIsLoading(false);
      }
    };

    login();
    return () => {
      isActive = false;
    };
  }, [navigate, queryClient, setAdminAuthToken, setEmailUserAuthToken]);

  if (isLoading) {
    return (
      <AuthLayout>
        <Box
          sx={{
            width: "100%",
            border: "1px dashed #d7d7d7",
            borderRadius: "8px",
            p: "20px",
            boxSizing: "border-box",
            color: "#5a5a5a",
            background: "#fafafa",
          }}
        >
          Signing you in with Google...
        </Box>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <Box
        sx={{
          border: "1px solid #ffcdd2",
          background: "#ffebee",
          color: "#b71c1c",
          borderRadius: "12px",
          p: 2,
          mb: 2,
        }}
      >
        {errorMessage || "Something went wrong"}
      </Box>
    </AuthLayout>
  );
}
